/**
 * The nightly base layer and the length-of-stay ladder.
 *
 * ---------------------------------------------------------------------------
 * THE SHAPE OF AN OPERATOR'S YEAR
 * ---------------------------------------------------------------------------
 *
 *   1. what does one night cost, across the season          -> nightly bands
 *   2. who may arrive and leave when, per period            -> on the band
 *   3. what is a week worth, a fortnight, three weeks       -> the ladder
 *
 * Nightly first because it is the basis of both models this system has to
 * serve: `dayTariff` underneath the legacy engine, and a per-night rate
 * underneath every channel. The ladder is then a discount ON that, which is
 * also how the market states it — "7+ nights, 10% off" — rather than as an
 * unrelated package price.
 *
 * ---------------------------------------------------------------------------
 * NO NEW ENGINE SEMANTICS
 * ---------------------------------------------------------------------------
 *
 * A nightly band IS a one-night rate over a date range. A ladder step IS an
 * n-night rate priced below n single nights. The engine already prices both,
 * and both are covered by the differential against the legacy calculator, so
 * nothing here needs the search to learn a new trick. `role` only tells the UI
 * how to draw them.
 *
 * ---------------------------------------------------------------------------
 * GENERATED RATES ARE PROPOSED, NEVER APPLIED
 * ---------------------------------------------------------------------------
 *
 * `proposeLadder` returns a diff. Nothing in this module writes to a programme.
 * Ids are deterministic so that regenerating produces "changed", not "removed
 * everything and added everything" — which would make the review screen useless
 * and train the operator to accept blindly.
 */

import type { IsoDate, Price, Programme, Rate, Weekday } from './authoring';
import { displayRole, rolesUnstated } from './roles';
import type { Money } from './types';
import { diffProgrammes, type ProgrammeDiff } from './versions';

export { nightlyOverlaps } from './authoring';
export type { NightlyOverlap } from './authoring';

export const LADDER_GENERATOR = 'los-ladder@1';
export const NIGHTLY_GENERATOR = 'nightly@1';

const MS = 86_400_000;
const dayOf = (iso: IsoDate) => Math.round(Date.parse(`${iso}T00:00:00Z`) / MS);

/** One stretch of the season where a night costs the same. */
export interface NightlyBand {
  from: IsoDate;
  /** Exclusive. */
  until: IsoDate;
  /** Price of one night. */
  price: number;
  /** Per-unit surcharges, e.g. `{ dog: 4 }`. */
  perUnit?: Record<string, number>;
  /**
   * Changeover days for a stay priced in this band.
   *
   * Whole-stay, not per night: "in summer you arrive on Saturday" is a fact
   * about the booking. Saying it per placement on a one-night rate would only
   * mean Saturday nights are priced here, which is not the same thing and not
   * what anybody means.
   */
  arriveOn?: Weekday[];
  departOn?: Weekday[];
  label?: string;
  /** Library period these dates were copied from. */
  derivedFrom?: string;
  /**
   * Where a discount priced from this band may be PLACED, if wider than the
   * band itself. Comes from the library period's `applies` window.
   */
  discountWindow?: { from: IsoDate; until: IsoDate };
}

/**
 * One length-of-stay entry.
 *
 * ---------------------------------------------------------------------------
 * WHY AN ENTRY IS AN OBJECT AND NOT A NUMBER
 * ---------------------------------------------------------------------------
 *
 * The first version of this was `{ nights, discountPct }` applied uniformly to
 * every band, which generates a tidy ladder and cannot express anything a real
 * operator actually wants: a fixed €399 midweek that is not a percentage of
 * anything, a seven-night discount that runs in July but not in August, two
 * different seven-night offers with different arrival windows.
 *
 * So an entry carries its own identity, its own arrival window and its own
 * price. Uniform ladders are still one line each — every field but `nights`
 * is optional — but the shape no longer stops at the easy case.
 */
export interface LadderStep {
  /**
   * Stable identity, so an entry survives editing, reordering and regeneration.
   * Optional only for the uniform case, where `nights` is identity enough.
   */
  id?: string;
  /** Exact length of the stay this entry prices. */
  nights: number;
  /** Percent off the nightly price for the whole stay. 10 means 10% off. */
  discountPct?: number;
  /**
   * A flat total for the whole stay, instead of a discount off nightly.
   *
   * Mutually exclusive with `discountPct`. This is what "7 nachten €399" is,
   * and expressing it as a percentage means recomputing the percentage every
   * time a nightly price moves — which is exactly when nobody remembers to.
   */
  price?: Money;
  /**
   * Arrivals this entry covers, intersected with the band's own window.
   *
   * Absent means the whole band, which is the common case. Present is how "the
   * long-stay deal runs in June and September but not in the school holidays"
   * gets said without inventing a second band.
   */
  arrive?: { from: IsoDate; until: IsoDate };
  /** Restrict to named nightly bands. Absent means every band. */
  bands?: readonly string[];
  label?: string;
}

/** Something wrong with a ladder, reported per entry. */
export interface LadderProblem {
  stepId: string;
  code: 'no-price' | 'two-prices' | 'bad-discount' | 'bad-price' | 'too-short'
    | 'overlapping-arrivals' | 'backwards-window';
  severity: 'error' | 'warning';
  message: string;
}

const stepId = (s: LadderStep, i: number) => s.id ?? `${s.nights}n-${i}`;

/**
 * Check a ladder before it generates anything.
 *
 * The interesting one is `overlapping-arrivals`. Two entries of the SAME
 * length whose arrival windows overlap are not a preference between two
 * prices — the engine takes the cheaper tiling, so the dearer entry silently
 * never applies, and the operator sees a price they did not write with no
 * indication of which entry produced it. That has to be caught here, while the
 * operator is still looking at the two entries that conflict.
 *
 * Different lengths overlapping is normal and expected: that IS a ladder.
 */
export function ladderProblems(steps: readonly LadderStep[]): LadderProblem[] {
  const problems: LadderProblem[] = [];

  steps.forEach((s, i) => {
    const id = stepId(s, i);
    const hasPct = s.discountPct !== undefined;
    const hasPrice = s.price !== undefined;
    if (hasPct && hasPrice) {
      problems.push({
        stepId: id, code: 'two-prices', severity: 'error',
        message: 'this entry has both a discount and a fixed price; it can only have one',
      });
    }
    if (!hasPct && !hasPrice) {
      problems.push({
        stepId: id, code: 'no-price', severity: 'error',
        message: 'this entry sets no price and no discount, so it would generate nothing',
      });
    }
    if (hasPct && !(s.discountPct! > 0 && s.discountPct! < 100)) {
      problems.push({
        stepId: id, code: 'bad-discount', severity: 'error',
        message: `a discount of ${s.discountPct}% is not between 0 and 100`,
      });
    }
    if (hasPrice && !(s.price! > 0)) {
      problems.push({
        stepId: id, code: 'bad-price', severity: 'error',
        message: 'a fixed price has to be above zero',
      });
    }
    if (!Number.isInteger(s.nights) || s.nights < 2) {
      problems.push({
        stepId: id, code: 'too-short', severity: 'error',
        message: 'a length-of-stay entry covers two nights or more; one night is the nightly price',
      });
    }
    if (s.arrive && s.arrive.until <= s.arrive.from) {
      problems.push({
        stepId: id, code: 'backwards-window', severity: 'error',
        message: `arrivals end on ${s.arrive.until}, which is not after ${s.arrive.from}`,
      });
    }
  });

  for (let i = 0; i < steps.length; i++) {
    for (let j = i + 1; j < steps.length; j++) {
      const a = steps[i]; const b = steps[j];
      if (a.nights !== b.nights) continue;
      if (a.bands && b.bands && !a.bands.some((x) => b.bands!.includes(x))) continue;
      const aFrom = a.arrive?.from ?? '0000-01-01';
      const aUntil = a.arrive?.until ?? '9999-12-31';
      const bFrom = b.arrive?.from ?? '0000-01-01';
      const bUntil = b.arrive?.until ?? '9999-12-31';
      if (aFrom < bUntil && bFrom < aUntil) {
        const from = aFrom > bFrom ? aFrom : bFrom;
        const until = aUntil < bUntil ? aUntil : bUntil;
        problems.push({
          stepId: stepId(b, j), code: 'overlapping-arrivals', severity: 'error',
          message: `"${b.label ?? stepId(b, j)}" and "${a.label ?? stepId(a, i)}" both price `
            + `${a.nights} nights for arrivals from ${from} to ${until}. The engine takes the `
            + 'cheaper one, so the dearer entry would never apply.',
        });
      }
    }
  }

  return problems;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Nightly bands as one-night rates.
 *
 * Ids key off the start date so regenerating the same calendar produces the
 * same ids and the diff stays readable.
 */
export function nightlyRates(bands: readonly NightlyBand[]): Rate[] {
  return bands.map((b) => {
    const price: Price = { base: round2(b.price) };
    if (b.perUnit && Object.keys(b.perUnit).length) price.perUnit = { ...b.perUnit };
    const rate: Rate = {
      id: `nightly-${b.from}`,
      label: b.label ?? `Night ${b.from}`,
      role: 'nightly',
      active: { from: b.from, until: b.until },
      nights: 1,
      price,
      origin: { generator: NIGHTLY_GENERATOR, from: 'nightly calendar' },
    };
    if (b.arriveOn) rate.stayArriveOn = [...b.arriveOn];
    if (b.departOn) rate.stayDepartOn = [...b.departOn];
    if (b.derivedFrom) rate.derivedFrom = b.derivedFrom;
    return rate;
  });
}

/**
 * The nightly rate covering a date — the MOST PRECISE one, if several do.
 *
 * Library periods overlap by design: "Zomer" is the whole summer and "Bouwvak"
 * is the busiest three weeks inside it. When both cover a night, the narrower
 * one is the one that was written about that night specifically, so it wins.
 *
 * Note this is a rule for BUILDING the calendar, not for pricing it. The engine
 * has no notion of precedence — it takes the cheapest tiling — so two nightly
 * rates overlapping in a finished programme is a modelling error, and
 * `nightlyOverlaps` reports it.
 */
export function nightlyOn(
  date: IsoDate, rates: readonly Rate[], infer = false,
): Rate | null {
  let best: Rate | null = null;
  let bestSpan = Infinity;
  for (const r of rates) {
    // `infer` is for LIFTED cards, which carry no roles at all: without it this
    // returns null for every date, `inferLadder` finds nothing, and the Ladder
    // tab shows invented defaults beside a real twelve-rate card. It never
    // writes the guess anywhere — see `roles.ts` for why it must not.
    if ((infer ? displayRole(r) : r.role) !== 'nightly') continue;
    if (date < r.active.from || date >= r.active.until) continue;
    const span = dayOf(r.active.until) - dayOf(r.active.from);
    if (span < bestSpan) { best = r; bestSpan = span; }
  }
  return best;
}

export interface LadderOptions {
  /**
   * Only generate a step where the whole stay fits inside one nightly band.
   *
   * On by default and it matters: a seven-night rate is priced from ONE nightly
   * figure, so if the week straddles a price change the discount is computed
   * off the wrong base. Straddling stays still price — the engine tiles them
   * from the nightly rates — they simply do not get a ladder discount.
   */
  requireWholeBand?: boolean;
  /** Round every generated price to whole euros. */
  roundToEuro?: boolean;
  /**
   * Per nightly-rate-id, the wider window a discount from that band may be
   * placed in — the library period's `applies` range.
   */
  discountWindows?: Record<string, { from: IsoDate; until: IsoDate }>;
}

/**
 * Turn nightly bands plus a discount ladder into rates.
 *
 * Each generated rate carries `minNights` equal to its own length. Without it
 * the engine would happily trim a seven-night rate to the three nights left at
 * the end of a band and still charge the seven-night price — the legacy
 * overhang behaviour, correct for a hand-written arrangement and badly wrong
 * for a generated discount.
 */
export function generateLadder(
  nightly: readonly Rate[],
  steps: readonly LadderStep[],
  opts: LadderOptions = {},
): Rate[] {
  const whole = opts.requireWholeBand ?? true;
  const windows = new Map(Object.entries(opts.discountWindows ?? {}));
  const out: Rate[] = [];

  const bands = nightly.filter((r) => r.role === 'nightly')
    .slice()
    .sort((a, b) => a.active.from.localeCompare(b.active.from));

  for (const band of bands) {
    const span = dayOf(band.active.until) - dayOf(band.active.from);
    const nightlyBase = band.price?.base ?? 0;
    if (nightlyBase <= 0) continue;

    // Sorted by length, ties broken by the order the operator wrote them, so
    // regenerating the same ladder produces the same rates in the same order
    // and the diff stays readable.
    const ordered = steps.map((s, i) => ({ s, i }))
      .sort((a, b) => a.s.nights - b.s.nights || a.i - b.i);

    // Where the next rung starts, so this one knows how far it reaches.
    const rungs = ordered
      .filter(({ s }) => !(s.bands && !s.bands.includes(band.id)))
      .map(({ s }) => s.nights);

    for (const { s: step } of ordered) {
      if (step.nights < 2) continue;             // a one-night "discount" is the nightly price
      if (whole && step.nights > span) continue; // will not fit in this band
      if (step.bands && !step.bands.includes(band.id)) continue;

      const pct = step.discountPct;
      const flat = step.price;
      if (flat === undefined && !(pct !== undefined && pct > 0)) continue; // nothing to express

      const price: Price = flat !== undefined
        ? { base: round2(flat) }
        : { base: opts.roundToEuro
          ? Math.round(nightlyBase * step.nights * (1 - pct! / 100))
          : round2(nightlyBase * step.nights * (1 - pct! / 100)) };
      // Per-unit surcharges follow the DISCOUNT, not the flat price. A fixed
      // "7 nights €399" is a statement about the pitch, and the operator who
      // wrote it did not thereby say anything about what a fourth person
      // costs, so those are carried across at full rate.
      if (band.price?.perUnit) {
        const factor = flat !== undefined ? 1 : 1 - pct! / 100;
        price.perUnit = Object.fromEntries(
          Object.entries(band.price.perUnit).map(([k, v]) => [k, round2(v * step.nights * factor)]),
        );
      }

      // A discount is placeable over the period's applicability window when it
      // has one — the park has always allowed an arrangement to reach into the
      // cheaper weeks either side of a holiday.
      const bandWindow = windows.get(band.id) ?? { from: band.active.from, until: band.active.until };
      // An entry's own arrival window narrows the band's; it never widens it,
      // because the band is what the price was computed from.
      const window = step.arrive
        ? {
          from: step.arrive.from > bandWindow.from ? step.arrive.from : bandWindow.from,
          until: step.arrive.until < bandWindow.until ? step.arrive.until : bandWindow.until,
        }
        : bandWindow;
      if (window.until <= window.from) continue;  // the two windows do not meet

      // ---------------------------------------------------------------
      // The discount has to hold BETWEEN the rungs, not only on them.
      // ---------------------------------------------------------------
      //
      // A rung that exists only at its own length produces a sawtooth. With a
      // nightly rate of €50, a week at −10% and a fortnight at −20%, the
      // engine tiles an eight-night stay as the week plus one full night, so
      // the effective discount runs 10% at seven nights, 8.8% at eight, 5.4%
      // at thirteen, then snaps to 20% at fourteen. Every length between two
      // rungs is dearer per night than the rung below it.
      //
      // Nobody writing "week −10%, fortnight −20%" means that. Worse, no
      // channel can be told it: an OTA discount rule is monotonic by
      // definition — "10% from seven nights" applies to every stay of seven
      // nights or more — so a sawtooth has to be exported as one explicit
      // entry per length, which is most of what made a converted card enormous.
      //
      // So a rung carries an extension at its own discounted nightly rate and
      // holds it until the next rung takes over. The effective discount is
      // then flat between rungs and steps down at each one, which is exactly
      // what the operator wrote and exactly what a channel can express.
      const nextRung = rungs.find((n) => n > step.nights);
      const perNight = flat !== undefined
        ? round2(flat / step.nights)
        : round2(nightlyBase * (1 - pct! / 100));
      const stepPrice: Price = { base: perNight };
      if (band.price?.perUnit) {
        const factor = flat !== undefined ? 1 : 1 - pct! / 100;
        stepPrice.perUnit = Object.fromEntries(
          Object.entries(band.price.perUnit).map(([k, v]) => [k, round2(v * factor)]),
        );
      }

      const how = flat !== undefined ? `€${round2(flat)}` : `${pct}% off`;
      const rate: Rate = {
        id: `ladder-${step.id ?? `${step.nights}n`}-${band.active.from}`,
        label: step.label
          ? `${step.label} · ${band.label ?? band.active.from}`
          : `${step.nights} nights · ${how} · ${band.label ?? band.active.from}`,
        role: 'ladder',
        active: { from: window.from, until: window.until },
        nights: step.nights,
        // Never let a generated discount be trimmed and charged in full.
        minNights: step.nights,
        price,
        // Bounded by the next rung, which prices those lengths better. The
        // last rung is left open so it reaches the end of the band.
        extend: {
          stepNights: 1,
          stepPrice,
          ...(nextRung === undefined ? {} : { maxSteps: nextRung - step.nights - 1 }),
        },
        origin: {
          generator: LADDER_GENERATOR,
          from: `${band.id} at ${how}`,
          basis: band.id,
        },
      };
      if (band.stayArriveOn) rate.stayArriveOn = [...band.stayArriveOn];
      if (band.stayDepartOn) rate.stayDepartOn = [...band.stayDepartOn];
      if (band.derivedFrom) rate.derivedFrom = band.derivedFrom;
      out.push(rate);
    }
  }
  return out;
}

export interface Leak {
  rateId: string;
  rateLabel: string;
  /** The nightly price the discount was computed from. */
  pricedFrom: number;
  /** The dearest nightly price its window reaches into. */
  reachesInto: number;
  dearerBandId: string;
  dearerBandLabel: string;
  /** What the stay costs under the discount vs. under that dearer band. */
  discountTotal: number;
  dearerTotal: number;
}

/**
 * Discounts that can be placed in a period dearer than the one they were priced
 * from.
 *
 * Applicability windows are set GENEROUSLY on purpose — often far wider than
 * the holiday needs — and that is fine by construction: a discount priced off
 * high-season nights can never win in a cheaper period, because the cheaper
 * nightly rates beat it outright. Nobody has to think about the width.
 *
 * The exception is the direction nobody thinks about either: a window reaching
 * into a DEARER band. A step priced off 40-a-night low season, placeable across
 * 70-a-night high season, undercuts the high season outright and every guest
 * finds it. That is the only case worth a warning, so it is the only case that
 * gets one — flagging merely-wide windows would be noise, and noise gets muted.
 *
 * A warning, not an error: an operator may want exactly this as a promotion.
 * But it should be a decision, and today nothing tells them.
 */
export function ladderLeakage(rates: readonly Rate[]): Leak[] {
  const nightly = rates.filter((r) => r.role === 'nightly');
  const out: Leak[] = [];

  for (const r of rates) {
    if (r.role !== 'ladder' && r.role !== 'arrangement') continue;
    const total = r.price?.base;
    if (total === undefined || total <= 0) continue;

    // The band the price was DERIVED from, which a generated rate records. It
    // is not the band covering `active.from`: a widened discount usually starts
    // in a cheaper neighbouring band, and reading the basis off the window made
    // every widened discount look like a leak.
    //
    // A hand-written arrangement has no basis, so fall back to the band at its
    // first night — the best available guess when nobody recorded the intent.
    const home = (r.origin?.basis
      ? nightly.find((n) => n.id === r.origin!.basis)
      : undefined) ?? nightlyOn(r.active.from, nightly);
    const homePrice = home?.price?.base;
    if (!homePrice || homePrice <= 0) continue;

    for (const band of nightly) {
      const bandPrice = band.price?.base;
      if (!bandPrice || bandPrice <= homePrice) continue;
      // Does the discount's window overlap this dearer band at all?
      if (r.active.until <= band.active.from || r.active.from >= band.active.until) continue;
      // ...and can a whole stay of this length actually sit inside it?
      const overlapFrom = r.active.from > band.active.from ? r.active.from : band.active.from;
      const overlapUntil = r.active.until < band.active.until ? r.active.until : band.active.until;
      if (dayOf(overlapUntil) - dayOf(overlapFrom) < r.nights) continue;

      out.push({
        rateId: r.id,
        rateLabel: r.label,
        pricedFrom: homePrice,
        reachesInto: bandPrice,
        dearerBandId: band.id,
        dearerBandLabel: band.label,
        discountTotal: round2(total),
        dearerTotal: round2(bandPrice * r.nights),
      });
      break; // one warning per rate is enough to act on
    }
  }
  return out;
}

export interface ObservedStep {
  nights: number;
  /** Median implied discount across the rates seen at this length. */
  discountPct: number;
  /** How many existing rates this was inferred from. */
  samples: number;
  /** The spread, so a wildly inconsistent length is visible rather than averaged away. */
  minPct: number;
  maxPct: number;
}

/**
 * What the existing card already charges, expressed as a ladder.
 *
 * This is the honest starting point for an operator converting a legacy set:
 * rather than inventing discounts, read back the ones already in force. A
 * seven-night arrangement at 390 against a 60-a-night base IS a 7% discount,
 * whether or not anyone has ever called it that.
 *
 * The spread is reported alongside the median because a length whose implied
 * discount ranges from 2% to 30% is not one ladder step — it is several
 * different products sharing a length, and flattening them would silently
 * reprice some of them.
 */
export function inferLadder(programme: Programme): ObservedStep[] {
  // A card that states no roles anywhere has simply never been asked — that is
  // every lifted legacy card. One that states some has been authored here, and
  // its silences mean something, so they are left alone.
  const infer = rolesUnstated(programme);
  const roleOf = (r: Rate) => (infer ? displayRole(r) : r.role);
  const nightly = programme.rates.filter((r) => roleOf(r) === 'nightly');
  const byNights = new Map<number, number[]>();

  for (const r of programme.rates) {
    if (roleOf(r) === 'nightly') continue;
    if (r.availability === 'closed' || r.availability === 'refuses') continue;
    if (r.nights < 2) continue;
    const base = r.price?.base;
    if (base === undefined || base <= 0) continue;

    const night = nightlyOn(
      r.active.from, nightly.length ? nightly : programme.rates, infer,
    );
    const nightBase = night?.price?.base;
    if (!nightBase || nightBase <= 0) continue;

    const full = nightBase * r.nights;
    const pct = ((full - base) / full) * 100;
    // A "discount" outside this range is not a length discount — it is a
    // different product (a premium arrangement, or a giveaway).
    if (pct < -50 || pct > 90) continue;
    const list = byNights.get(r.nights);
    if (list) list.push(pct); else byNights.set(r.nights, [pct]);
  }

  const out: ObservedStep[] = [];
  for (const [nights, pcts] of byNights) {
    pcts.sort((a, b) => a - b);
    const mid = Math.floor(pcts.length / 2);
    const median = pcts.length % 2 ? pcts[mid] : (pcts[mid - 1] + pcts[mid]) / 2;
    out.push({
      nights,
      discountPct: round2(median),
      samples: pcts.length,
      minPct: round2(pcts[0]),
      maxPct: round2(pcts[pcts.length - 1]),
    });
  }
  out.sort((a, b) => a.nights - b.nights);
  return out;
}

export interface LadderProposal {
  proposed: Programme;
  diff: ProgrammeDiff;
  /** Generated rates a human has since edited; these are never overwritten silently. */
  protected: string[];
  /**
   * Protected rungs whose GROUND HAS MOVED since they were overruled.
   *
   * An override is relative even when it looks absolute: €275 against a
   * generated €300 is nine per cent off, and the same €275 against a generated
   * €345 is twenty. Both are "protected" and both look identical on screen, so
   * without this an operator cannot tell whether their decision still means what
   * they meant by it.
   *
   * Empty where the base has not moved, and — deliberately — where the rung
   * predates the recording of it. An old card cannot answer this question and
   * saying nothing is the honest version of that.
   */
  drifted: Array<{
    id: string;
    /** What the generator produced when the operator first overruled it. */
    was: number;
    /** What it produces now. */
    now: number;
  }>;
}

/**
 * Propose a regenerated ladder for review.
 *
 * Returns the candidate programme and the diff against the current one. It does
 * not apply anything — `applyRateChanges` does that, with the ids the operator
 * accepted.
 *
 * Rates outside the generator's own role are carried through untouched: the
 * ladder generator owns `role: 'ladder'` and nothing else, so a hand-written
 * arrangement is never a casualty of regenerating week discounts.
 */
export function proposeLadder(
  current: Programme,
  steps: readonly LadderStep[],
  opts: LadderOptions = {},
): LadderProposal {
  const nightly = current.rates.filter((r) => r.role === 'nightly');
  const untouched = current.rates.filter((r) => r.role !== 'ladder');
  const generated = generateLadder(nightly, steps, opts);

  const proposed: Programme = { ...current, rates: [...untouched, ...generated] };
  const diff = diffProgrammes(current, proposed);
  const guardedRates = current.rates.filter((r) => r.role === 'ladder' && r.origin?.editedByHand);
  const guarded = guardedRates.map((r) => r.id);

  // Compared against what the generator produces NOW for the same rung, by id.
  // Only `base` — a per-unit surcharge moving is a different conversation, and
  // reporting it here would bury the one number an operator acts on.
  const byId = new Map(generated.map((g) => [g.id, g]));
  const drifted: LadderProposal['drifted'] = [];
  for (const r of guardedRates) {
    const then = r.origin?.generatedPrice?.base;
    const now = byId.get(r.id)?.price?.base;
    if (typeof then !== 'number' || typeof now !== 'number') continue;
    if (Math.abs(then - now) <= 0.005) continue;
    drifted.push({ id: r.id, was: round2(then), now: round2(now) });
  }

  return { proposed, diff, protected: guarded, drifted };
}

/**
 * Split a season into nightly bands at every boundary the library implies.
 *
 * Convenience for seeding a calendar: give it the season and the periods that
 * fall inside it, and it returns contiguous bands, each carrying the id of the
 * period that produced its boundary. The operator then prices them.
 */
export function bandsFromPeriods(
  season: { from: IsoDate; until: IsoDate },
  periods: ReadonlyArray<{ id: string; label: string; from: IsoDate; until: IsoDate }>,
  basePrice: number,
): NightlyBand[] {
  const start = dayOf(season.from);
  const end = dayOf(season.until);
  if (end <= start) return [];

  const cuts = new Set<number>([start, end]);
  for (const p of periods) {
    const a = dayOf(p.from);
    const b = dayOf(p.until);
    if (a > start && a < end) cuts.add(a);
    if (b > start && b < end) cuts.add(b);
  }
  const ordered = [...cuts].sort((a, b) => a - b);
  const iso = (d: number) => new Date(d * MS).toISOString().slice(0, 10);

  const bands: NightlyBand[] = [];
  for (let i = 0; i < ordered.length - 1; i++) {
    const from = iso(ordered[i]);
    const until = iso(ordered[i + 1]);
    // Periods overlap on purpose — the whole summer, and the three busiest
    // weeks inside it. The narrower one was written about these nights
    // specifically, so it takes precedence. Taking the first match instead
    // would hand the band to whichever period happened to be listed earlier.
    let covering: (typeof periods)[number] | undefined;
    let bestSpan = Infinity;
    for (const p of periods) {
      if (from < p.from || from >= p.until) continue;
      const span = dayOf(p.until) - dayOf(p.from);
      if (span < bestSpan) { covering = p; bestSpan = span; }
    }
    bands.push({
      from,
      until,
      price: basePrice,
      label: covering ? covering.label : `Season ${from}`,
      derivedFrom: covering?.id,
      discountWindow: covering && 'applies' in covering
        ? (covering as { applies?: { from: IsoDate; until: IsoDate } }).applies
        : undefined,
    });
  }
  return bands;
}
